import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { FaChevronDown, FaChevronRight, FaSun } from 'react-icons/fa';
import { getSummerSportsRegistrations } from '../../../services/summerSportsService';

const itemsPerPage = 15;

const extractArrayData = (response) => {
    if (Array.isArray(response)) return response;
    if (Array.isArray(response?.data)) return response.data;
    return [];
};

const calculateAge = (birthDate) => {
    if (!birthDate) return '-';
    const today = new Date();
    const date = new Date(birthDate);
    let age = today.getFullYear() - date.getFullYear();
    const monthDiff = today.getMonth() - date.getMonth();
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < date.getDate())) {
        age -= 1;
    }
    return age;
};

const formatDate = (value) => {
    if (!value) return '-';
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return '-';
    return date.toLocaleDateString('tr-TR');
};

const formatIdentityNumber = (value) => {
    if (!value) return '-';
    const digits = String(value).replace(/\D/g, '');
    return digits.length === 11 ? digits : value;
};

const formatPhone = (value) => {
    if (!value) return '-';
    const digits = String(value).replace(/\D/g, '');
    if (digits.length === 11) {
        return `${digits.slice(0, 4)} ${digits.slice(4, 7)} ${digits.slice(7, 9)} ${digits.slice(9)}`;
    }
    if (digits.length === 10) {
        return `${digits.slice(0, 3)} ${digits.slice(3, 6)} ${digits.slice(6, 8)} ${digits.slice(8)}`;
    }
    return value;
};

const parseSelections = (item) => {
    if (Array.isArray(item?.selectedBranches)) return item.selectedBranches;
    if (Array.isArray(item?.SelectedBranches)) return item.SelectedBranches;

    const rawJson = item?.selectedBranchesJson ?? item?.SelectedBranchesJson;
    if (!rawJson) return [];

    try {
        const parsed = JSON.parse(rawJson);
        return Array.isArray(parsed) ? parsed : [];
    } catch {
        return [];
    }
};

const normalizeRegistration = (item) => ({
    formId: item?.formId ?? item?.FormId,
    childFirstName: item?.childFirstName ?? item?.ChildFirstName ?? '',
    childLastName: item?.childLastName ?? item?.ChildLastName ?? '',
    childIdentityNumber: item?.childIdentityNumber ?? item?.ChildIdentityNumber ?? '',
    childBirthDate: item?.childBirthDate ?? item?.ChildBirthDate ?? null,
    guardianFirstName: item?.guardianFirstName ?? item?.GuardianFirstName ?? '',
    guardianLastName: item?.guardianLastName ?? item?.GuardianLastName ?? '',
    guardianIdentityNumber: item?.guardianIdentityNumber ?? item?.GuardianIdentityNumber ?? '',
    guardianPhone: item?.guardianPhone ?? item?.GuardianPhone ?? '',
    guardianRelationship: item?.guardianRelationship ?? item?.GuardianRelationship ?? '',
    address: item?.address ?? item?.Address ?? '',
    selectedBranchesSummary: item?.selectedBranchesSummary ?? item?.SelectedBranchesSummary ?? '',
    selectedBranches: parseSelections(item),
    createdDate: item?.createdDate ?? item?.CreatedDate ?? null
});

const groupSelections = (selectedBranches) => {
    return selectedBranches.reduce((acc, item) => {
        const facilityName = item?.facilityName ?? item?.FacilityName ?? 'Bilinmeyen Tesis';
        const branchName = item?.branchName ?? item?.BranchName ?? 'Bilinmeyen Branş';

        if (!acc[facilityName]) {
            acc[facilityName] = [];
        }

        if (!acc[facilityName].includes(branchName)) {
            acc[facilityName].push(branchName);
        }

        return acc;
    }, {});
};

const renderInfoStack = (primary, secondary) => (
    <div className="summer-info-stack">
        <span className="summer-info-primary">{primary || '-'}</span>
        {secondary && <span className="summer-info-secondary">{secondary}</span>}
    </div>
);

const renderSelectionGroups = (groupedSelections, compact = false) => (
    <div className={`summer-selection-groups ${compact ? 'is-compact' : ''}`}>
        {Object.entries(groupedSelections).map(([facilityName, branches]) => (
            <div className="summer-selection-group" key={facilityName}>
                <div className="summer-selection-facility">{facilityName}</div>
                <div className="summer-selection-branches">
                    {branches.map((branch) => (
                        <span className="summer-selection-chip" key={`${facilityName}-${branch}`}>
                            {branch}
                        </span>
                    ))}
                </div>
            </div>
        ))}
        {Object.keys(groupedSelections).length === 0 && (
            <span className="summer-empty-selection">Seçim bulunamadı.</span>
        )}
    </div>
);

const getBranchName = (selection) =>
    selection?.branchName ?? selection?.BranchName ?? 'Bilinmeyen Branş';

const SummerSportsApplicationsList = ({ showToast }) => {
    const [applications, setApplications] = useState([]);
    const [loading, setLoading] = useState(false);
    const [searchQuery, setSearchQuery] = useState('');
    const [sortOrder, setSortOrder] = useState('newest');
    const [selectedBranch, setSelectedBranch] = useState('');
    const [currentPage, setCurrentPage] = useState(1);
    const [apiError, setApiError] = useState('');
    const showToastRef = useRef(showToast);
    const hasLoadedRef = useRef(false);

    useEffect(() => {
        showToastRef.current = showToast;
    }, [showToast]);

    const loadData = useCallback(async () => {
        if (loading) {
            return;
        }

        setLoading(true);
        setApiError('');

        try {
            const response = await getSummerSportsRegistrations();
            const data = extractArrayData(response).map(normalizeRegistration);
            setApplications(data);
        } catch (error) {
            console.error('Summer sports registrations load failed:', error);
            const status = error?.response?.status;
            const message = status === 404
                ? 'Yaz Kursları endpointi sunucuda bulunamadı. Backend yayına alınmamış veya route henüz deploy edilmemiş olabilir.'
                : error?.message || 'Yaz Kursları başvuruları alınamadı.';
            setApplications([]);
            setApiError(message);
            if (showToastRef.current) {
                showToastRef.current(message, 'error');
            }
        } finally {
            setLoading(false);
        }
    }, [loading]);

    useEffect(() => {
        if (hasLoadedRef.current) {
            return;
        }

        hasLoadedRef.current = true;
        loadData();
    }, [loadData]);

    const filteredApplications = useMemo(() => {
        const query = searchQuery.trim().toLowerCase();

        const filtered = applications.filter((item) => {
            if (selectedBranch) {
                const hasSelectedBranch = item.selectedBranches.some((selection) => getBranchName(selection) === selectedBranch);
                if (!hasSelectedBranch) return false;
            }

            if (!query) return true;

            const searchableSelections = item.selectedBranches
                .map((selection) => `${selection?.facilityName ?? selection?.FacilityName ?? ''} ${selection?.branchName ?? selection?.BranchName ?? ''}`)
                .join(' ');

            return [
                `${item.childFirstName} ${item.childLastName}`,
                item.childIdentityNumber,
                `${item.guardianFirstName} ${item.guardianLastName}`,
                item.guardianIdentityNumber,
                item.guardianPhone,
                item.guardianRelationship,
                item.address,
                item.selectedBranchesSummary,
                searchableSelections
            ]
                .join(' ')
                .toLowerCase()
                .includes(query);
        });

        filtered.sort((a, b) => {
            const dateA = new Date(a.createdDate || 0).getTime();
            const dateB = new Date(b.createdDate || 0).getTime();
            return sortOrder === 'newest' ? dateB - dateA : dateA - dateB;
        });

        return filtered;
    }, [applications, searchQuery, selectedBranch, sortOrder]);

    const paginatedApplications = useMemo(() => {
        const startIndex = (currentPage - 1) * itemsPerPage;
        return filteredApplications.slice(startIndex, startIndex + itemsPerPage);
    }, [filteredApplications, currentPage]);

    const totalPages = Math.ceil(filteredApplications.length / itemsPerPage) || 1;

    useEffect(() => {
        setCurrentPage(1);
    }, [searchQuery, selectedBranch, sortOrder]);

    const branchCards = useMemo(() => {
        const branchMap = new Map();

        applications.forEach((application) => {
            const uniqueBranches = new Set(application.selectedBranches.map(getBranchName));

            uniqueBranches.forEach((branchName) => {
                if (!branchMap.has(branchName)) {
                    branchMap.set(branchName, {
                        name: branchName,
                        applicationCount: 0,
                        selectionCount: 0
                    });
                }

                branchMap.get(branchName).applicationCount += 1;
            });

            application.selectedBranches.forEach((selection) => {
                const branchName = getBranchName(selection);
                if (!branchMap.has(branchName)) {
                    branchMap.set(branchName, {
                        name: branchName,
                        applicationCount: 0,
                        selectionCount: 0
                    });
                }

                branchMap.get(branchName).selectionCount += 1;
            });
        });

        return Array.from(branchMap.values()).sort((a, b) => {
            if (b.applicationCount !== a.applicationCount) {
                return b.applicationCount - a.applicationCount;
            }

            return a.name.localeCompare(b.name, 'tr');
        });
    }, [applications]);

    const totalSelections = useMemo(
        () => applications.reduce((sum, item) => sum + item.selectedBranches.length, 0),
        [applications]
    );

    const totalFacilities = useMemo(() => {
        const facilities = new Set();
        applications.forEach((item) => {
            item.selectedBranches.forEach((selection) => {
                const facilityName = selection?.facilityName ?? selection?.FacilityName;
                if (facilityName) {
                    facilities.add(facilityName);
                }
            });
        });
        return facilities.size;
    }, [applications]);

    return (
        <div className="appointments-list-section summer-applications-section">
            <div className="section-header">
                <div className="section-header-left">
                    <h3 className="summer-section-title">
                        <FaSun /> Yaz Kursları Başvuruları
                    </h3>
                    <span className="appointment-count">{filteredApplications.length} Başvuru</span>
                </div>
                <div className="section-header-right" style={{ display: 'flex', gap: '10px', alignItems: 'center', flexWrap: 'wrap' }}>
                    <div className="summer-summary-pills">
                        <span>{applications.length} Kayıt</span>
                        <span>{totalSelections} Branş</span>
                        <span>{totalFacilities} Tesis</span>
                    </div>
                    <button className="refresh-button" onClick={loadData} disabled={loading}>
                        {loading ? 'Yükleniyor...' : 'Yenile'}
                    </button>
                </div>
            </div>

            {branchCards.length > 0 && (
                <div className="summer-branch-filter-strip">
                    <button
                        type="button"
                        className={`summer-branch-card ${selectedBranch === '' ? 'is-active' : ''}`}
                        onClick={() => setSelectedBranch('')}
                    >
                        <span className="summer-branch-name">Tüm Branşlar</span>
                        <strong>{applications.length}</strong>
                    </button>
                    {branchCards.map((branch) => (
                        <button
                            type="button"
                            key={branch.name}
                            className={`summer-branch-card ${selectedBranch === branch.name ? 'is-active' : ''}`}
                            onClick={() => setSelectedBranch((current) => (current === branch.name ? '' : branch.name))}
                        >
                            <span className="summer-branch-name">{branch.name}</span>
                            <strong>{branch.applicationCount}</strong>
                        </button>
                    ))}
                </div>
            )}

            <div className="appointments-filter-bar">
                <div className="filter-search">
                    <input
                        type="text"
                        placeholder="Çocuk, veli, T.C., telefon veya branş ara..."
                        className="filter-search-input"
                        value={searchQuery}
                        onChange={(event) => setSearchQuery(event.target.value)}
                    />
                    <select
                        className="sort-select"
                        value={sortOrder}
                        onChange={(event) => setSortOrder(event.target.value)}
                    >
                        <option value="newest">En Yeni Önce</option>
                        <option value="oldest">En Eski Önce</option>
                    </select>
                </div>
            </div>

            {apiError && (
                <div style={{
                    margin: '16px 20px 0',
                    padding: '12px 14px',
                    borderRadius: '10px',
                    background: '#fee2e2',
                    border: '1px solid #fecaca',
                    color: '#b91c1c',
                    fontSize: '0.9rem',
                    fontWeight: 600
                }}>
                    {apiError}
                </div>
            )}

            <div className="appointments-table-wrapper desktop-only">
                <table className="appointments-table">
                    <thead>
                        <tr>
                            <th>Çocuk</th>
                            <th>Çocuk T.C.</th>
                            <th>Yaş / Doğum</th>
                            <th>Veli</th>
                            <th>Veli T.C.</th>
                            <th>Telefon</th>
                            <th>Yakınlık</th>
                            <th>Branş Seçimleri</th>
                            <th>Adres</th>
                            <th>Kayıt Tarihi</th>
                        </tr>
                    </thead>
                    <tbody>
                        {paginatedApplications.map((application) => {
                            const groupedSelections = groupSelections(application.selectedBranches);

                            return (
                                <tr key={application.formId}>
                                    <td className="name-cell summer-person-cell">
                                        {renderInfoStack(`${application.childFirstName} ${application.childLastName}`, `Form #${application.formId || '-'}`)}
                                    </td>
                                    <td className="summer-nowrap-cell">{formatIdentityNumber(application.childIdentityNumber)}</td>
                                    <td>
                                        {renderInfoStack(`${calculateAge(application.childBirthDate)} Yaş`, formatDate(application.childBirthDate))}
                                    </td>
                                    <td className="name-cell summer-person-cell">
                                        {renderInfoStack(`${application.guardianFirstName} ${application.guardianLastName}`, application.guardianRelationship || '-')}
                                    </td>
                                    <td className="summer-nowrap-cell">{formatIdentityNumber(application.guardianIdentityNumber)}</td>
                                    <td className="summer-nowrap-cell">{formatPhone(application.guardianPhone)}</td>
                                    <td>{application.guardianRelationship || '-'}</td>
                                    <td className="summer-selections-cell">
                                        {renderSelectionGroups(groupedSelections)}
                                    </td>
                                    <td className="summer-address-cell">
                                        {application.address || '-'}
                                    </td>
                                    <td className="summer-nowrap-cell">{formatDate(application.createdDate)}</td>
                                </tr>
                            );
                        })}

                        {!loading && filteredApplications.length === 0 && (
                            <tr>
                                <td colSpan="10" className="no-data">Başvuru bulunamadı.</td>
                            </tr>
                        )}

                        {loading && (
                            <tr>
                                <td colSpan="10" className="no-data">Kayıtlar yükleniyor...</td>
                            </tr>
                        )}
                    </tbody>
                </table>
            </div>

            {/* Mobile Card View */}
            <div className="appointments-cards-mobile">
                {paginatedApplications.map((application) => {
                    const groupedSelections = groupSelections(application.selectedBranches);
                    return (
                        <div key={application.formId} className="appointment-card">
                            <div className="card-header">
                                <span className="card-name">
                                    {application.childFirstName} {application.childLastName}
                                </span>
                                <span className="summer-age-pill">
                                    {calculateAge(application.childBirthDate)} Yaş
                                </span>
                            </div>
                            <div className="card-body">
                                <div className="card-info-row">
                                    <span className="card-label">Çocuk T.C.</span>
                                    <span className="card-value">{formatIdentityNumber(application.childIdentityNumber)}</span>
                                </div>
                                <div className="card-info-row">
                                    <span className="card-label">Doğum</span>
                                    <span className="card-value">{formatDate(application.childBirthDate)}</span>
                                </div>
                                <div className="card-info-row">
                                    <span className="card-label">Veli</span>
                                    <span className="card-value">{application.guardianFirstName} {application.guardianLastName}</span>
                                </div>
                                <div className="card-info-row">
                                    <span className="card-label">Yakınlık</span>
                                    <span className="card-value">{application.guardianRelationship || '-'}</span>
                                </div>
                                <div className="card-info-row">
                                    <span className="card-label">Veli T.C.</span>
                                    <span className="card-value">{formatIdentityNumber(application.guardianIdentityNumber)}</span>
                                </div>
                                <div className="card-info-row">
                                    <span className="card-label">Telefon</span>
                                    <span className="card-value">{formatPhone(application.guardianPhone)}</span>
                                </div>
                                <div className="card-info-row" style={{ gridColumn: '1 / -1' }}>
                                    <span className="card-label">Branş Seçimleri</span>
                                    {renderSelectionGroups(groupedSelections, true)}
                                </div>
                                <div className="card-info-row" style={{ gridColumn: '1 / -1' }}>
                                    <span className="card-label">Adres</span>
                                    <span className="card-value">{application.address || '-'}</span>
                                </div>
                            </div>
                            <div className="card-footer">
                                <span className="card-date">{formatDate(application.createdDate)}</span>
                            </div>
                        </div>
                    );
                })}
                {!loading && filteredApplications.length === 0 && (
                    <div className="no-data-card">Başvuru bulunamadı.</div>
                )}
                {loading && (
                    <div className="no-data-card">Kayıtlar yükleniyor...</div>
                )}
            </div>

            {totalPages > 1 && (
                <div className="pagination-wrapper">
                    <button
                        className="pagination-btn pagination-nav-btn"
                        onClick={() => setCurrentPage((prev) => Math.max(1, prev - 1))}
                        disabled={currentPage === 1}
                    >
                        <FaChevronDown style={{ transform: 'rotate(90deg)' }} />
                    </button>
                    <div className="pagination-pages">
                        {Array.from({ length: totalPages }, (_, index) => index + 1)
                            .filter((page) => page === 1 || page === totalPages || Math.abs(page - currentPage) <= 1)
                            .map((page, index, array) => (
                                <React.Fragment key={page}>
                                    {index > 0 && array[index - 1] !== page - 1 && <span className="pagination-ellipsis">...</span>}
                                    <button
                                        className={`pagination-btn ${currentPage === page ? 'active' : ''}`}
                                        onClick={() => setCurrentPage(page)}
                                    >
                                        {page}
                                    </button>
                                </React.Fragment>
                            ))}
                    </div>
                    <button
                        className="pagination-btn pagination-nav-btn"
                        onClick={() => setCurrentPage((prev) => Math.min(totalPages, prev + 1))}
                        disabled={currentPage === totalPages}
                    >
                        <FaChevronRight />
                    </button>
                </div>
            )}
        </div>
    );
};

export default SummerSportsApplicationsList;
